import React, { useState, useEffect, useRef, createContext } from "react";

// import our utility functions for working with filters and languages
import { i18n, useUrlParams } from "./util/i18n";

// Service to fetch (filtered) jobs from the backend
import { loadFilters, loadJobs } from "./util/JobQueryService";

// Specific hooks
import { useLocalStorage } from "react-use";

// Context
import LocalStorageContext from "./util/LocalStorageContext";

// Our reusable components
import FilterSection from "./components/FilterSection";
import JobList from "./components/JobList";
import NoJobsFound from "./components/NoJobsFound";
import JobListPagination from "./components/JobListPagination";

// MUI components and other imports
import {
    Grid,
    LinearProgress,
    Skeleton,
    Switch,
    Typography,
} from "@mui/material";
import Container from "@mui/material/Container";
import FavouriteCounterButton from "./components/FavouriteCounterButton";
import settings from "./settings";
import JobGrid from "./components/JobGrid";

/**
 *
 *
 */
function App() {
    // 1. Base URL for this project / environment.
    const baseUrl = settings.baseUrl; // "https://stage.ohws.prospective.ch/public/v1/medium/1003009";
    const language = i18n.lang;

    // 2. define constants for filter ids here
    const FILTER = settings.filterIds;

    // 3. specify the page size for the job list
    const JOBS_PAGE_SIZE = settings.jobsPageSize || 5;
    // This will get updated with the filters, required for pagination, jump to page etc.
//    const [totalJobs, setTotalJobs] = useState(0);

    // 4. Here we define the filter GET parameters for the job query service
    // (also for your reference how the object looks like)
    const defaultFilterParamsFromUrl = useUrlParams();

    const DEFAULT_FILTER_PARAMS = {
        lang: language,
        q: null,
        offset: 0,
        limit: JOBS_PAGE_SIZE,
        workload: null,
        // "f":
        //   { 80: ["1003009","...."],
        //     10: ["...", ""]
        // attribute-based filters
    };

    const [filterParams, setFilterParams] = useState(defaultFilterParamsFromUrl);
    /**useEffect(() => {
        if (defaultFilterParamsFromUrl) {
            console.log ("FilterParams from Url called")
            setFilterParams(defaultFilterParamsFromUrl);
        }
    }, [defaultFilterParamsFromUrl]);
**/

    const createFetchUrl = (baseUrl, params) => {
        // A bit hacky: "f:<filterId1>=123,456&f:<filterId2>=888,999&...""
        // TODO clean this up
        return (
            `${baseUrl}/jobs?` +
            Object.keys(params)
                .filter((key) => params[key] || key == "offset") // always add offset
                .map((key) => {
                    return key == "f"
                        ? Object.entries(params[key])
                            .map((e) => `f=${e[0]}:${e[1].join(",")}`)
                            .join("&")
                        : `${key}=${params[key]}`;
                })
                .join("&")
        );
    };

    // This is the current, full URL for the job GET request
    const [jobListFetchUrl, setJobListFetchUrl] = useState(createFetchUrl(settings.baseUrl, DEFAULT_FILTER_PARAMS));

    // whenever the filterParams change, we need to update the jobListFetchUrl.
    // filterParams will change when the user changes the filters, or when the URL changes (defaultFilterParamsFromUrl)
    useEffect(() => {
        // initially we don't have any filterParams, so we don't want to update the jobListFetchUrl
        console.log ("FilterParams",filterParams)
        if (filterParams && filterParams.limit) {
            if (settings.autoSubmit || !data || filterParams.submitted) {
                let url = createFetchUrl(baseUrl, filterParams);
                if (url != jobListFetchUrl) {
                    console.log("Filterparams changed, new URL: ", filterParams, url);
                    setJobListFetchUrl(url);
                    setFilterParams((currentParams) => {  return {...currentParams, ...{submitted: false} }});
                }
            }
        }
    }, [filterParams]);

    // Local storage state for the job favourites
    const [localStorageFavourites, setLocalStorageFavourites] = useLocalStorage(
        settings.localStorageKeyFavourites,
        {}
    );

    const { isLoading, isError, data } = loadJobs(jobListFetchUrl);

    // Load available filter options (usually only once)
    const {
        isLoading: isLoadingFilters,
        isError: isErrorFilters,
        data: currentFilters,
    } = loadFilters(baseUrl, language);

    /***
     *
     * Return the JSX for the App
     *
     *
     */

    const [displayGrid, setDisplayGrid] = useState(settings.jobGrid);

    return (
        <LocalStorageContext.Provider
            value={[localStorageFavourites, setLocalStorageFavourites]}
        >
            <Container maxWidth="xl">
                {isLoadingFilters && <LinearProgress />}
                {isError && <p>Error loading Jobs: {isError} ...</p>}
                {currentFilters && (
                    <>
                        <FilterSection
                            filterParams={filterParams}
                            setFilterParams={setFilterParams}
                            attributes={currentFilters.attributes}
                        />
                    </>
                )}
                {settings.enableFavourites && (
                    <Grid container justifyContent="flex-end" spacing={4}>
                        <Grid item xs={4} sm={2} className="job-favourite-button">
                            <FavouriteCounterButton loading={!data} />
                        </Grid>
                    </Grid>
                )}

                {/* Unncomment if the feature needed
          {i18n.translations["displayAsGrid"]}
          <Switch onChange={() => setDisplayGrid(!displayGrid)}></Switch>
          */}

                <div className="jobsList">
                    {isLoading && <LinearProgress />}
                    {data && data.total === 0 && (
                        <>
                            <NoJobsFound />
                        </>
                    )}

                    {data &&
                        data.total > 0 &&
                        (displayGrid ? (
                            <JobGrid
                                jobs={data.jobs}
                                totalJobs={data.total}
                                locationAttribute={FILTER["Arbeitsort"]}
                                berufsGruppeAttribute={FILTER["Berufsgruppe"]}
                                klinikBereichAttribute={FILTER["KlinikBereich"]}
                                favourites={settings.enableFavourites ? "toggle" : "none"}
                            />
                        ) : (
                            <JobList
                                jobs={data.jobs}
                                totalJobs={totalJobs}
                                locationAttribute={FILTER["Arbeitsort"]}
                                berufsGruppeAttribute={FILTER["Berufsgruppe"]}
                                klinikBereichAttribute={FILTER["KlinikBereich"]}
                                favourites={settings.enableFavourites ? "toggle" : "none"}
                            />
                        ))}
                    {data && data.total > 0 && (
                        <JobListPagination
                            attributes={currentFilters.attributes}
                            filterParams={filterParams}
                            setFilterParams={setFilterParams}
                            totalJobs={data.total}
                        />
                    )}

                    {!data && (
                        <>
                            <Skeleton
                                sx={{ bgcolor: "white" }}
                                variant="rectangular"
                                width={"100%"}
                                height={"500px"}
                            />
                        </>
                    )}
                </div>
            </Container>
        </LocalStorageContext.Provider>
    );
}
export default App;